import {
  ColDef,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import LogFileCellRenderer from 'components/common/logFileCellRenderer'
import { formatDate } from 'utils/helpers'

/**
 * Export run time: "45 s", "1 min 5 s", "1 h 12 min". Logs saved before
 * completion time was recorded, and exports still running, show '-'.
 */
const formatCompletionTime = (ms: number | undefined) => {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return '-'

  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds} s`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ${seconds % 60} s`

  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}

/**
 * Applied export filters as "key: value" pairs. Long lists (barcodes from an
 * uploaded file) show as a count, or as the first few values in the tooltip.
 * Logs saved before filters were recorded have none, and show '-'.
 */
const formatFilters = (
  filters: Record<string, unknown> | undefined,
  listLimit: number,
) => {
  if (!filters) return '-'

  const entries = Object.entries(filters)
  if (!entries.length) return 'None'

  return entries
    .map(([key, value]) => {
      if (!Array.isArray(value)) return `${key}: ${value}`
      if (value.length <= listLimit) return `${key}: ${value.join(', ')}`
      return listLimit
        ? `${key}: ${value.slice(0, listLimit).join(', ')} … (${
            value.length
          } total)`
        : `${key}: ${value.length} values`
    })
    .join(' | ')
}

/**
 * render column name in export logs
 */
export const getColumns = (page: number) =>
  [
    {
      headerName: 'ID',
      field: 'uuid',
      valueFormatter: (params: ValueFormatterParams) => {
        return (params.node?.rowIndex as number) + 1 + page
      },
      maxWidth: 120,
    },
    { headerName: 'User', field: 'user' },
    { headerName: 'Export Module', field: 'module' },
    {
      headerName: 'File',
      field: 'file',
      cellRenderer: (params: ICellRendererParams) => (
        <LogFileCellRenderer params={params} />
      ),
    },
    {
      headerName: 'Date/Time',
      field: 'createdAt',
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value, { includeTime: true }),
    },
    { headerName: 'Status', field: 'status' },
    {
      headerName: 'Filters',
      field: 'filters',
      minWidth: 200,
      valueFormatter: (params: ValueFormatterParams) =>
        formatFilters(params.value, 3),
      tooltipValueGetter: (params: ITooltipParams) =>
        params.value ? formatFilters(params.value, 10) : undefined,
    },
    {
      headerName: 'Completion Time',
      field: 'durationMs',
      valueFormatter: (params: ValueFormatterParams) =>
        formatCompletionTime(params.value),
    },
  ] as ColDef[]
